import React, { useState, useEffect, useRef } from "react";

/**
 * v6 F42k v14e/v14f — Uniforme inline werkregel-omschrijving-editor.
 *
 * v14f: single-line default (h-8) met auto-grow tot max 2 regels wanneer
 * de tekst effectief langer is. Zelfde compacte visuele stijl als de
 * prijs-input. Autosave op blur; leegmaken == reset; ↶ Herstel-knop
 * naast "Aangepast" badge (32px tap-target).
 */
export function EditableDescription({
  value,
  override,
  defaultValue,
  onOverride,
  onReset,
  testId,
  className = "",
}) {
  const hasOverride =
    override !== null && override !== undefined && String(override).length > 0;
  const effective = hasOverride ? String(override) : (value ?? defaultValue ?? "");
  const [draft, setDraft] = useState(effective);
  const taRef = useRef(null);

  useEffect(() => {
    setDraft(effective);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effective]);

  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const h = Math.min(el.scrollHeight, 56);
    el.style.height = h + "px";
  }, [draft]);

  const commit = () => {
    const next = (draft ?? "").trim();
    const defTrim = (defaultValue ?? "").trim();
    if (!next || next === defTrim) {
      if (hasOverride) onReset?.();
    } else if (next !== String(override ?? "").trim()) {
      onOverride?.(next);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Escape") {
      setDraft(effective);
      e.currentTarget.blur();
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  return (
    <div className={`flex flex-wrap items-start gap-1.5 w-full min-w-0 ${className}`}>
      <textarea
        ref={taRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        rows={1}
        placeholder={defaultValue || "Omschrijving"}
        className={
          "flex-1 min-w-0 text-sm px-2 py-1 rounded-md border resize-none overflow-hidden leading-tight " +
          "bg-white/70 focus:bg-white focus:outline-none focus:ring-1 " +
          (hasOverride
            ? "border-[#B07A4F] focus:border-[#B07A4F] focus:ring-[#B07A4F] text-[#2C2C2A]"
            : "border-[#E5E2DC] hover:border-[#C9C8C2] focus:border-[#B07A4F] focus:ring-[#B07A4F]")
        }
        style={{ height: "32px", minHeight: "32px", maxHeight: "56px" }}
        data-testid={testId ? `${testId}-editor` : undefined}
      />
      <span className="sr-only" data-testid={testId ? `${testId}-text` : undefined}>
        {effective}
      </span>
      {hasOverride && (
        <div className="flex items-center gap-1 flex-shrink-0 basis-full sm:basis-auto mt-0.5">
          <span
            className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F2DCB2] text-[#8C5F3E] font-semibold uppercase tracking-wide"
            data-testid={testId ? `${testId}-badge` : undefined}
          >
            Aangepast
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onReset?.(); }}
            className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#B07A4F] hover:text-[#8C5F3E] hover:underline px-2 py-1.5 min-h-[32px] rounded"
            title="Herstel standaardomschrijving"
            aria-label="Herstel standaardomschrijving"
            data-testid={testId ? `${testId}-reset-btn` : undefined}
          >
            ↶ Herstel
          </button>
        </div>
      )}
    </div>
  );
}

export default EditableDescription;
